import { z } from "zod";
import {
  agentTypeSchema,
  agentConnectionConfigSchema,
  agentConfigSchemas,
  type AgentTypeKey,
} from "../../../config/agent-registry";

// Base schema for all agent connections
export const baseAgentConnection = z.object({
  id: z.uuid(),
  agentId: z.uuid(),
  name: z.string().min(1, "Required"),
  description: z.string().min(1, "Required"),
  type: z.string(),
  enabled: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Re-export from agent-types config for backward compatibility
export const agentConnectionType = agentTypeSchema;
export const agentConnectionConfig = agentConnectionConfigSchema;

// Full agent connection schema
export const agentConnection = baseAgentConnection.extend({
  type: agentConnectionType,
  config: z.any(), // We'll validate this dynamically based on type
});

// Create schema (without id, timestamps)
export const createAgentConnection = z.object({
  agentId: z.uuid(),
  name: z.string().min(1, "Required"),
  description: z.string().min(1, "Required"),
  type: agentConnectionType,
  config: z.any(), // We'll validate this dynamically based on type
  enabled: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  userGroupIds: z.array(z.uuid()).min(1, "At least one user group must be selected"), // Required, at least one group
});

// Update schema (all optional except required validations)
export const updateAgentConnection = z.object({
  name: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  config: z.any().optional(), // We'll validate this dynamically based on type
  enabled: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  userGroupIds: z.array(z.uuid()).optional(), // Optional group assignments
});

// Query filters
export const findAgentConnectionsFilters = z.object({
  page: z.coerce.number().default(1),
  per_page: z.coerce.number().default(10),
  filter: z
    .object({
      agentId: z.uuid().optional(),
      type: agentConnectionType.optional(),
      enabled: z.boolean().optional(),
      search: z.string().optional(),
    })
    .optional(),
});

// Response schemas
export const findAgentConnections = z.object({
  data: z.array(agentConnection),
  count: z.number(),
});

// Dynamic validation functions
export const validateAgentConnectionConfig = (type: AgentTypeKey, config: any) => {
  const schema = agentConfigSchemas[type];
  if (!schema) {
    throw new Error(`Unknown agent type: ${type}`);
  }
  return schema.parse(config);
};

export const validateCreateAgentConnection = (data: any) => {
  // First validate the base structure
  const baseValidated = createAgentConnection.parse(data);
  
  // Then validate the config against the specific type schema
  const validatedConfig = validateAgentConnectionConfig(baseValidated.type as AgentTypeKey, baseValidated.config);
  
  return {
    ...baseValidated,
    config: validatedConfig,
  };
};

export const validateUpdateAgentConnection = (data: any, existingType?: AgentTypeKey) => {
  // First validate the base structure
  const baseValidated = updateAgentConnection.parse(data);
  
  // If config is provided and we have a type (either from data or existing), validate it
  if (baseValidated.config && existingType) {
    const validatedConfig = validateAgentConnectionConfig(existingType, baseValidated.config);
    return {
      ...baseValidated,
      config: validatedConfig,
    };
  }
  
  return baseValidated;
};

// Inferred types
export type AgentConnection = z.infer<typeof agentConnection>;
export type CreateAgentConnection = z.infer<typeof createAgentConnection>;
export type UpdateAgentConnection = z.infer<typeof updateAgentConnection>;
export type FindAgentConnectionsFilters = z.infer<
  typeof findAgentConnectionsFilters
>;

export type AgentConnectionConfig = z.infer<typeof agentConnectionConfig>;

// Validator object for export
export const agentConnectionValidators = {
  baseAgentConnection,
  agentConnection,
  createAgentConnection,
  updateAgentConnection,
  findAgentConnectionsFilters,
  findAgentConnections,

  agentConnectionConfig,
  agentConnectionType,

  // Dynamic validation functions
  validateAgentConnectionConfig,
  validateCreateAgentConnection,
  validateUpdateAgentConnection,
};
